import {
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { interval, combineLatest, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { PrintersStatus } from 'src/app/models/printing.model';
import { LocalStorageService } from 'src/app/common/services/localStorage.service';
import { UtilService } from 'src/app/util/util.service';
import { PrinterStatusService } from '../../services/printer-status.service';
import { REACT_DEVICE } from 'src/app/common/enums/react-device.enum';
import { PrinterConst } from '../constants/printer.const';
import {
  CONNECTED_ICON,
  DISCONNECTED_ICON,
  CLOSE_PRINTER,
  PRINTER_DISCONNECTED_ICON,
  RIGHT_ARROW_ICON_BLUE,
  RIGHT_ARROW_ICON_GREEN,
  PRINTER_DELAY_ICON,
  PRINTER_DELAY_ICON_GREEN,
  PRINTER_LIST_ICON_GREEN,
} from 'src/defaultImages.js';
@Component({
  selector: 'printer-status-widget',
  templateUrl: './printer-status-widget.component.html',
  styleUrls: ['./printer-status-widget.component.css'],
})
export class PrinterStatusWidgetComponent implements OnInit, OnDestroy {
  images = {
    CONNECTED_ICON: CONNECTED_ICON,
    DISCONNECTED_ICON: DISCONNECTED_ICON,
    CLOSE_PRINTER: CLOSE_PRINTER,
    RIGHT_ARROW_ICON_BLUE: RIGHT_ARROW_ICON_BLUE,
    RIGHT_ARROW_ICON_GREEN: RIGHT_ARROW_ICON_GREEN,
    PRINTER_DELAY_ICON: PRINTER_DELAY_ICON,
    PRINTER_DISCONNECTED_ICON: PRINTER_DISCONNECTED_ICON,
    PRINTER_LIST_ICON_GREEN: PRINTER_LIST_ICON_GREEN,
    PRINTER_DELAY_ICON_GREEN: PRINTER_DELAY_ICON_GREEN,
  };
  totalData = {
    totalCount: 0,
    totalDisconnectedPrinter: 0,
    totalDelayPrintEvents: 0,
  };
  popoverCardData = [
    {
      connectionIconGreen: this.images.PRINTER_DELAY_ICON_GREEN,
      connectionIconBlue: this.images.PRINTER_DELAY_ICON,
      totalCountData: this.totalData.totalDelayPrintEvents,
      noDataTitle: 'WoW! 🤩 No print delays detected',
      noDataDescription: 'Orders not printed within 3 mins will show here.',
      dataTitle: 'Delay in Order Printing',
      dataDescription: 'Orders not printed within 3 mins will show here.',
      actionIconGreen: this.images.RIGHT_ARROW_ICON_GREEN,
      actionIconBlue: this.images.RIGHT_ARROW_ICON_BLUE,
    },
    {
      connectionIconGreen: this.images.PRINTER_LIST_ICON_GREEN,
      connectionIconBlue: this.images.PRINTER_DISCONNECTED_ICON,
      totalCountData: this.totalData.totalDisconnectedPrinter,
      noDataTitle: 'All printers are connected! 🖨️ 😃',
      noDataDescription: 'All printers are currently connected to your device.',
      dataTitle: 'Printer Disconnected',
      dataDescription: 'Some printers are not connected. View details.',
      actionIconGreen: this.images.RIGHT_ARROW_ICON_GREEN,
      actionIconBlue: this.images.RIGHT_ARROW_ICON_BLUE,
    },
  ];

  private subscription: Subscription;
  allPrintersActive: boolean = false;
  allPrintersList: PrintersStatus[] = [];
  allPrintsEventData = [];
  isRealmNotEnabledAndReactDevice: boolean = false;
  isDelayPrintingEnabled: boolean = false;
  isPrintStatusBtnVisible: boolean = false;
  translationText: any;

  // animation
  interValsubscription: Subscription;
  showAnimation = false;
  animationClassTimeout: ReturnType<typeof setTimeout>;
  isShakeEnabled: boolean = false;
  animationTimeout = PrinterConst.timeout;
  animationDuration = PrinterConst.duration;

  constructor(
    private _modalService: NzModalService,
    private _localStorage: LocalStorageService,
    private _utilService: UtilService,
    private _printerStatusService: PrinterStatusService,
    private _translateService: TranslateService,
    private _cdr: ChangeDetectorRef,
    private _ngZone: NgZone
  ) {
    this.watchRestSettings();
    this.getAnimationSettings();
  }

  ngOnInit(): void {
    this._translateService.get(['PRINTER_STATUS']).subscribe((translation) => {
      this.translationText = translation.PRINTER_STATUS;
      this.setTranslations();
    });
    this.subscribeToUpdatedData();

    const isRealmEnabled = !!JSON.parse(
      this._localStorage.get('isRealmEnabled') || '0'
    );
    this.isRealmNotEnabledAndReactDevice =
      !isRealmEnabled &&
      this._localStorage.get('is_react') === REACT_DEVICE.REACT_DEVICE;
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.stopMonitoring();
  }

  subscribeToUpdatedData() {
    this.subscription = combineLatest([
      this._printerStatusService.allPrintersList$,
      this._printerStatusService.allPrintersActive$,
      this._printerStatusService.allDelayPrintEvents$,
    ]).subscribe(
      ([newAllPrintersList, newAllPrintersActive, newAllPrintsEvent]) => {
        this.allPrintersList = newAllPrintersList || [];
        this.allPrintersActive = newAllPrintersActive;
        this.allPrintsEventData = newAllPrintsEvent;
        this.updateCount();
        this.updateMonitoringForAnimation();
      }
    );
  }

  updateMonitoringForAnimation(): void {
    if (!this.allPrintersActive && !this.interValsubscription) {
      this.startMonitoring();
    } else if (this.allPrintersActive) {
      this.stopMonitoring();
    }
  }

  watchRestSettings() {
    this._localStorage.watch('restaurant_settings').subscribe((value) => {
      if (this._printerStatusService.checkDelayPrintingEnabled()) {
        this.isDelayPrintingEnabled = true;
      } else {
        this.isDelayPrintingEnabled = false;
      }
      this.isPrintStatusBtnVisible =
        this._printerStatusService.checkPrintStatusBtnVisible();
    });
  }

  setTranslations() {
    this.popoverCardData = [
      {
        connectionIconGreen: this.images.PRINTER_DELAY_ICON_GREEN,
        connectionIconBlue: this.images.PRINTER_DELAY_ICON,
        totalCountData: this.totalData.totalDelayPrintEvents,
        noDataTitle: this.translationText.WOW_NO_PRINT_DELAY_DETECTED,
        noDataDescription:
          this.translationText.ORDERS_NOT_PRINTED_WITHIN_3_MIN_WILL_SHOW,
        dataTitle: this.translationText.DELAY_IN_ORDER_PRINTING,
        dataDescription:
          this.translationText.ORDERS_NOT_PRINTED_WITHIN_3_MIN_WILL_SHOW,
        actionIconGreen: this.images.RIGHT_ARROW_ICON_GREEN,
        actionIconBlue: this.images.RIGHT_ARROW_ICON_BLUE,
      },
      {
        connectionIconGreen: this.images.PRINTER_LIST_ICON_GREEN,
        connectionIconBlue: this.images.PRINTER_DISCONNECTED_ICON,
        totalCountData: this.totalData.totalDisconnectedPrinter,
        noDataTitle: this.translationText.ALL_PRINTERS_ARE_CONNECTED,
        noDataDescription:
          this.translationText
            .ALL_PRINTERS_ARE_CURRENTLY_CONNECTED_TO_YOUR_DEVICE,
        dataTitle: this.translationText.PRINTER_DISCONNECTED,
        dataDescription:
          this.translationText.SOME_PRINTERS_ARE_NOT_CONNECTED_VIEW_DETAILS,
        actionIconGreen: this.images.RIGHT_ARROW_ICON_GREEN,
        actionIconBlue: this.images.RIGHT_ARROW_ICON_BLUE,
      },
    ];
  }

  updateCount() {
    this.totalData.totalDisconnectedPrinter =
      this._printerStatusService.totalDisconnectedPrinter() || 0;
    this.totalData.totalDelayPrintEvents =
      this._printerStatusService.totalDelayPrintEvents() || 0;
    this.totalData.totalCount =
      this.totalData.totalDisconnectedPrinter +
      this.totalData.totalDelayPrintEvents;

    // Update the specific value (totalCountData in popover card)
    this.popoverCardData[0].totalCountData =
      this.totalData.totalDelayPrintEvents;
    this.popoverCardData[1].totalCountData =
      this.totalData.totalDisconnectedPrinter;
    this._cdr.detectChanges();
  }

  performAction(param) {
    if (param === 0) {
      if (this.totalData.totalDelayPrintEvents === 0) {
        this.callNoDelayEventsModal();
      } else {
        this._printerStatusService.openDelayPrintingModal();
      }
    } else {
      this._printerStatusService.openAllPrintersModal();
    }
  }

  onlyOpenAllPrintersModal() {
    this._printerStatusService.openAllPrintersModal();
  }

  async callNoDelayEventsModal() {
    const { NoDelayEventsModalComponent } = await import(
      /* webpackPrefetch: true */ '../no-delay-events-modal/no-delay-events-modal.component'
    );
    this._modalService.create({
      nzClosable: false,
      nzContent: NoDelayEventsModalComponent,
      nzBodyStyle: { padding: '0px' },
      nzFooter: null,
      nzWidth: 510,
    });
  }

  getAnimationSettings() {
    const restSettings = this._utilService.getRestaurantSettings();
    const globalSetting = (restSettings || {}).global || {};
    const shakeTimeout =
      Number(globalSetting.shake_timeout) || PrinterConst.timeout;
    const shakeDuration =
      Number(globalSetting.shake_duration) || PrinterConst.duration;

    // Extracting settings or defaulting to a large value
    this.animationTimeout = Math.min(shakeTimeout, PrinterConst.maxValue);
    this.animationDuration = Math.min(shakeDuration, PrinterConst.maxValue);

    if (this.animationTimeout < this.animationDuration) {
      this.animationDuration = PrinterConst.duration;
    }

    // Assigning the values to the animation settings
    this.isShakeEnabled = !!globalSetting.shake_enabled;
  }

  startMonitoring() {
    this.startShowingAnimation(); // fist time show animation
    // Set up an interval to check printer status every minute (adjust as needed)
    const timeInterval = this.animationTimeout + this.animationDuration;
    this.interValsubscription = interval(timeInterval).subscribe(() => {
      this.startShowingAnimation();
    });
  }

  stopMonitoring() {
    // Unsubscribe from the interval when not needed anymore
    if (this.interValsubscription) {
      this.interValsubscription.unsubscribe();
    }
  }

  startShowingAnimation() {
    this._ngZone.run(() => {
      this.showAnimation = true;

      // Set a timeout to remove the class after 10 seconds
      if (!this.animationClassTimeout) {
        this.animationClassTimeout = setTimeout(() => {
          this.showAnimation = false;
          this._cdr.detectChanges(); // Trigger change detection to apply the changes immediately
          this.animationClassTimeout = null;
        }, this.animationDuration);
      }
    });
  }
}